/**
 * Knowledge Base — the conceptual layer of frontier AI: who, what's the
 * shape of the threat, what to read, how the timelines look.
 *
 * Tabs:
 *   - People       /api/frontier/profiles
 *   - Threat models /api/frontier/threat-models
 *   - Canon (books) /api/frontier/books
 *   - Projections   /api/frontier/projections
 */
import { useEffect, useState } from 'react'
import {
  Users, AlertTriangle, BookOpen, Telescope, RefreshCw, ExternalLink,
  Database, ClipboardCheck, ScrollText, Sparkles, Boxes, Network,
  Bot, FlaskConical, Unlock, Bug, GraduationCap, HeartPulse,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type Tab = 'profiles' | 'threats' | 'books' | 'projections' | 'papers' | 'datasets' | 'evals' | 'moments' | 'open_models' | 'coalitions' | 'robotics' | 'scientific' | 'jailbreaks' | 'cyber' | 'education' | 'healthcare'

interface Profile {
  id: string; name: string; role: string; tier: string; country: string; summary: string
}
interface ThreatModel {
  id: string; name: string; category: string; first_named: string; summary: string; url: string
}
interface Book {
  id: string; title: string; author: string; year: number; category: string; summary: string; url: string
}
interface Projection {
  id: string; source: string; date_published: string; horizon_year: number
  claim: string; summary: string; url: string
}
interface LandmarkPaper {
  id: string; title: string; authors: string; year: number; venue: string
  category: string; summary: string; url: string
}
interface Dataset {
  id: string; name: string; released: string; size: string
  category: string; stewards: string; summary: string; url: string
}
interface EvalTool {
  id: string; name: string; stewards: string; category: string
  summary: string; url: string
}
interface CapabilityMoment {
  id: string; date: string; moment: string; lab: string; category: string; summary: string
}
interface OpenModel {
  id: string; name: string; lab: string; released: string
  params: string; context: string; licence: string; summary: string
}
interface Coalition {
  id: string; name: string; founded: string; members: string
  category: string; summary: string; url: string
}
interface RoboticsPlatform {
  id: string; platform: string; company: string; country: string
  category: string; summary: string; url: string
}
interface ScientificAI {
  id: string; name: string; lab: string; released: string
  domain: string; category: string; summary: string; url: string
}
interface Jailbreak {
  id: string; name: string; first_seen: string; category: string
  summary: string; url?: string
}
interface CyberAI {
  id: string; name: string; first_seen: string; category: string
  summary: string; url?: string
}
interface DomainItem {
  id: string; name: string; category: string; country?: string
  released?: string; summary: string; url?: string
}

const TIER_TONE: Record<string, string> = {
  founder:    'bg-purple/15 text-purple-light',
  godfather:  'bg-amber/15 text-amber-light',
  safety:     'bg-rose/15 text-rose-light',
  researcher: 'bg-blue/15 text-blue-light',
  exec:       'bg-emerald/15 text-emerald',
}

const TABS: { key: Tab; label: string; icon: typeof Users; tone: string }[] = [
  { key: 'profiles',    label: 'People',         icon: Users,           tone: 'text-purple-light' },
  { key: 'papers',      label: 'Landmark Papers',icon: ScrollText,      tone: 'text-blue-light' },
  { key: 'threats',     label: 'Threat Models',  icon: AlertTriangle,   tone: 'text-rose-light' },
  { key: 'books',       label: 'Canon',          icon: BookOpen,        tone: 'text-amber-light' },
  { key: 'projections', label: 'Projections',    icon: Telescope,       tone: 'text-emerald' },
  { key: 'datasets',    label: 'Datasets',       icon: Database,        tone: 'text-cyan' },
  { key: 'evals',       label: 'Evals Tooling',  icon: ClipboardCheck,  tone: 'text-amber-light' },
  { key: 'moments',     label: 'Capability Moments', icon: Sparkles,    tone: 'text-rose-light' },
  { key: 'open_models', label: 'Open Models',    icon: Boxes,           tone: 'text-emerald' },
  { key: 'coalitions',  label: 'Coalitions',     icon: Network,         tone: 'text-purple-light' },
  { key: 'robotics',    label: 'Robotics',       icon: Bot,             tone: 'text-amber-light' },
  { key: 'scientific',  label: 'Scientific AI',  icon: FlaskConical,    tone: 'text-emerald' },
  { key: 'jailbreaks',  label: 'Jailbreaks',     icon: Unlock,          tone: 'text-rose-light' },
  { key: 'cyber',       label: 'Cyber AI',       icon: Bug,             tone: 'text-rose-light' },
  { key: 'education',   label: 'Education AI',   icon: GraduationCap,   tone: 'text-blue-light' },
  { key: 'healthcare',  label: 'Healthcare AI',  icon: HeartPulse,      tone: 'text-emerald' },
]

export default function KnowledgeBasePage() {
  const [tab, setTab] = useState<Tab>('profiles')
  const [data, setData] = useState<Record<Tab, any>>({} as any)
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/frontier/profiles').then(r => ['profiles', r.data] as const).catch(() => ['profiles', null] as const),
      api.get('/api/frontier/threat-models').then(r => ['threats', r.data] as const).catch(() => ['threats', null] as const),
      api.get('/api/frontier/books').then(r => ['books', r.data] as const).catch(() => ['books', null] as const),
      api.get('/api/frontier/projections').then(r => ['projections', r.data] as const).catch(() => ['projections', null] as const),
      api.get('/api/frontier/landmark-papers').then(r => ['papers', r.data] as const).catch(() => ['papers', null] as const),
      api.get('/api/frontier/datasets').then(r => ['datasets', r.data] as const).catch(() => ['datasets', null] as const),
      api.get('/api/frontier/evals-tooling').then(r => ['evals', r.data] as const).catch(() => ['evals', null] as const),
      api.get('/api/frontier/capability-moments').then(r => ['moments', r.data] as const).catch(() => ['moments', null] as const),
      api.get('/api/frontier/open-models').then(r => ['open_models', r.data] as const).catch(() => ['open_models', null] as const),
      api.get('/api/frontier/coalitions').then(r => ['coalitions', r.data] as const).catch(() => ['coalitions', null] as const),
      api.get('/api/frontier/robotics').then(r => ['robotics', r.data] as const).catch(() => ['robotics', null] as const),
      api.get('/api/frontier/scientific-ai').then(r => ['scientific', r.data] as const).catch(() => ['scientific', null] as const),
      api.get('/api/frontier/jailbreaks').then(r => ['jailbreaks', r.data] as const).catch(() => ['jailbreaks', null] as const),
      api.get('/api/frontier/cyber-ai').then(r => ['cyber', r.data] as const).catch(() => ['cyber', null] as const),
      api.get('/api/frontier/education-ai').then(r => ['education', r.data] as const).catch(() => ['education', null] as const),
      api.get('/api/frontier/healthcare-ai').then(r => ['healthcare', r.data] as const).catch(() => ['healthcare', null] as const),
    ]).then(rs => {
      const out: Record<Tab, any> = {} as any
      for (const [k, v] of rs) if (v) (out as any)[k] = v
      setData(out)
    }).finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Users size={18} className="text-purple-light" /> KNOWLEDGE BASE
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          people · threat models · canon · forecasts
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map(t => {
          const Icon = t.icon
          const active = tab === t.key
          const counts = {
            profiles:    data.profiles?.count,
            threats:     data.threats?.count,
            books:       data.books?.count,
            projections: data.projections?.count,
            papers:      data.papers?.count,
            datasets:    data.datasets?.count,
            evals:       data.evals?.count,
            moments:     data.moments?.count,
            open_models: data.open_models?.count,
            coalitions:  data.coalitions?.count,
            robotics:    data.robotics?.count,
            scientific:  data.scientific?.count,
            jailbreaks:  data.jailbreaks?.count,
            cyber:       data.cyber?.count,
            education:   data.education?.count,
            healthcare:  data.healthcare?.count,
          }
          return (
            <button key={t.key} type="button" onClick={() => setTab(t.key)}
              className={clsx(
                'px-3 py-1.5 text-xs flex items-center gap-1.5 border-b-2 transition-colors',
                active ? 'border-purple text-text-primary'
                       : 'border-transparent text-text-muted hover:text-text-primary',
              )}>
              <Icon size={12} className={active ? t.tone : ''} />
              <span>{t.label}</span>
              <span className="text-[10px] font-mono opacity-70">{counts[t.key] ?? '—'}</span>
            </button>
          )
        })}
      </div>

      {tab === 'profiles'    && <ProfilesView    rows={data.profiles?.profiles || []} />}
      {tab === 'threats'     && <ThreatsView     rows={data.threats?.threat_models || []} />}
      {tab === 'books'       && <BooksView       rows={data.books?.books || []} />}
      {tab === 'projections' && <ProjectionsView rows={data.projections?.projections || []} />}
      {tab === 'papers'      && <PapersView      rows={data.papers?.papers || []} />}
      {tab === 'datasets'    && <DatasetsView    rows={data.datasets?.datasets || []} />}
      {tab === 'evals'       && <EvalsView       rows={data.evals?.tools || []} />}
      {tab === 'moments'     && <MomentsView     rows={data.moments?.moments || []} />}
      {tab === 'open_models' && <OpenModelsView  rows={data.open_models?.models || []} />}
      {tab === 'coalitions'  && <CoalitionsView  rows={data.coalitions?.coalitions || []} />}
      {tab === 'robotics'    && <RoboticsView    rows={data.robotics?.platforms || []} />}
      {tab === 'scientific'  && <ScientificView  rows={data.scientific?.systems || []} />}
      {tab === 'jailbreaks'  && <JailbreaksView  rows={data.jailbreaks?.jailbreaks || []} />}
      {tab === 'cyber'       && <CyberAIView     rows={data.cyber?.items || []} />}
      {tab === 'education'   && <DomainItemsView rows={data.education?.items || []} tone="blue-light" />}
      {tab === 'healthcare'  && <DomainItemsView rows={data.healthcare?.items || []} tone="emerald" />}
    </div>
  )
}

function DomainItemsView({ rows, tone }: { rows: DomainItem[]; tone: string }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(item => (
        <article key={item.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
            {item.released && (
              <span className="text-[11px] font-mono text-amber-light">{item.released}</span>
            )}
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{item.name}</h3>
            <span className={clsx('badge text-[9px] shrink-0',
              tone === 'emerald' ? 'bg-emerald/15 text-emerald border border-emerald/30'
                                  : 'bg-blue/15 text-blue-light border border-blue/30')}>
              {item.category}
            </span>
          </div>
          {item.country && (
            <div className="text-[10px] text-text-muted mb-2 font-mono">{item.country}</div>
          )}
          <p className="text-xs text-text-secondary leading-relaxed">{item.summary}</p>
          {item.url && (
            <a href={item.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
              Reference <ExternalLink size={10} />
            </a>
          )}
        </article>
      ))}
    </div>
  )
}

function CyberAIView({ rows }: { rows: CyberAI[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(c => (
        <article key={c.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{c.first_seen}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{c.name}</h3>
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30 shrink-0">{c.category}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{c.summary}</p>
          {c.url && (
            <a href={c.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
              Reference <ExternalLink size={10} />
            </a>
          )}
        </article>
      ))}
    </div>
  )
}

function JailbreaksView({ rows }: { rows: Jailbreak[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(j => (
        <article key={j.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{j.first_seen}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{j.name}</h3>
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30 shrink-0">{j.category}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{j.summary}</p>
          {j.url && (
            <a href={j.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
              Reference <ExternalLink size={10} />
            </a>
          )}
        </article>
      ))}
    </div>
  )
}

function RoboticsView({ rows }: { rows: RoboticsPlatform[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map(r => (
        <article key={r.id} className="card p-3">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary flex-1">{r.platform}</h3>
            <span className="text-[10px] text-text-muted">{r.country}</span>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/30">{r.category}</span>
            <span className="badge text-[9px] bg-surface-raised text-text-muted">{r.company}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{r.summary}</p>
          <a href={r.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function ScientificView({ rows }: { rows: ScientificAI[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(s => (
        <article key={s.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{s.released}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{s.name}</h3>
            <span className="badge text-[9px] bg-emerald/15 text-emerald border border-emerald/30 shrink-0">{s.domain}</span>
          </div>
          <div className="text-[10px] text-purple-light mb-2">{s.lab} · {s.category}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{s.summary}</p>
          <a href={s.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function OpenModelsView({ rows }: { rows: OpenModel[] }) {
  return (
    <div className="card p-0 overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-surface-raised">
          <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <th className="text-left px-3 py-2">Released</th>
            <th className="text-left px-3 py-2">Model</th>
            <th className="text-left px-3 py-2">Lab</th>
            <th className="text-left px-3 py-2">Params</th>
            <th className="text-left px-3 py-2">Context</th>
            <th className="text-left px-3 py-2">Licence</th>
            <th className="text-left px-3 py-2">Summary</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(m => (
            <tr key={m.id} className="hover:bg-surface-raised/40">
              <td className="px-3 py-1.5 font-mono text-text-muted">{m.released}</td>
              <td className="px-3 py-1.5 font-semibold text-emerald">{m.name}</td>
              <td className="px-3 py-1.5 text-purple-light">{m.lab}</td>
              <td className="px-3 py-1.5 font-mono text-amber-light">{m.params}</td>
              <td className="px-3 py-1.5 font-mono text-blue-light">{m.context}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary">{m.licence}</td>
              <td className="px-3 py-1.5 text-[11px] text-text-secondary leading-tight">{m.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CoalitionsView({ rows }: { rows: Coalition[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {rows.map(c => (
        <article key={c.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{c.name}</h3>
            <span className="badge text-[9px] bg-purple/15 text-purple-light border border-purple/30 shrink-0">{c.category}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-2 font-mono">
            est. {c.founded} · {c.members}
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{c.summary}</p>
          <a href={c.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function MomentsView({ rows }: { rows: CapabilityMoment[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(m => (
        <article key={m.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{m.date}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{m.moment}</h3>
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30 shrink-0">{m.category}</span>
          </div>
          <div className="text-[10px] text-purple-light mb-2">{m.lab}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{m.summary}</p>
        </article>
      ))}
    </div>
  )
}

function PapersView({ rows }: { rows: LandmarkPaper[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(p => (
        <article key={p.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{p.year}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{p.title}</h3>
            <span className="badge text-[9px] bg-blue/15 text-blue-light border border-blue/30 shrink-0">{p.category}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-2">
            {p.authors} · <span className="text-purple-light">{p.venue}</span>
          </div>
          <p className="text-xs text-text-secondary leading-relaxed">{p.summary}</p>
          <a href={p.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function DatasetsView({ rows }: { rows: Dataset[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(d => (
        <article key={d.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{d.name}</h3>
            <span className="badge text-[9px] bg-cyan/15 text-cyan border border-cyan/30 shrink-0">{d.category}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-1 font-mono">{d.released} · {d.size}</div>
          <div className="text-[10px] text-purple-light mb-2">{d.stewards}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{d.summary}</p>
          <a href={d.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function EvalsView({ rows }: { rows: EvalTool[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {rows.map(e => (
        <article key={e.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{e.name}</h3>
            <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/30 shrink-0">{e.category}</span>
          </div>
          <div className="text-[10px] text-purple-light mb-2">{e.stewards}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{e.summary}</p>
          <a href={e.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function ProfilesView({ rows }: { rows: Profile[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map(p => (
        <article key={p.id} className="card p-3">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary">{p.name}</h3>
            <span className="text-[10px] text-text-muted">{p.country}</span>
            <span className={clsx('badge text-[9px] ml-auto', TIER_TONE[p.tier] || 'bg-surface-raised text-text-muted')}>
              {p.tier}
            </span>
          </div>
          <div className="text-[10px] text-purple-light mb-1.5 font-mono">{p.role}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{p.summary}</p>
        </article>
      ))}
    </div>
  )
}

function ThreatsView({ rows }: { rows: ThreatModel[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(t => (
        <article key={t.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-2 flex-wrap">
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{t.name}</h3>
            <span className="badge text-[9px] bg-rose/15 text-rose-light border border-rose/30 shrink-0">{t.category}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-2">First named: {t.first_named}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{t.summary}</p>
          <a href={t.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function BooksView({ rows }: { rows: Book[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {rows.map(b => (
        <article key={b.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{b.year}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{b.title}</h3>
            <span className="badge text-[9px] bg-amber/15 text-amber-light border border-amber/30 shrink-0">{b.category}</span>
          </div>
          <div className="text-[10px] text-text-muted mb-2">{b.author}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{b.summary}</p>
          <a href={b.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            More <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}

function ProjectionsView({ rows }: { rows: Projection[] }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map(p => (
        <article key={p.id} className="card p-4">
          <div className="flex items-baseline gap-2 mb-1 flex-wrap">
            <span className="text-[11px] font-mono text-amber-light">{p.date_published}</span>
            <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{p.source}</h3>
            <span className="badge text-[9px] bg-emerald/15 text-emerald border border-emerald/30 shrink-0">→ {p.horizon_year}</span>
          </div>
          <div className="text-xs font-semibold text-emerald mb-1.5">{p.claim}</div>
          <p className="text-xs text-text-secondary leading-relaxed">{p.summary}</p>
          <a href={p.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
            Reference <ExternalLink size={10} />
          </a>
        </article>
      ))}
    </div>
  )
}
